/**
 * Flatten a bulk-upload error entry into a single readable string.
 *
 * Backend bulk-upload endpoints occasionally return errors as structured
 * objects (e.g. `{row, employee_id, code, field, message,
 * matched_employee_code}` from `/api/employees/bulk-upload`). Rendering
 * such an object directly as a React child throws error #31 ("Objects are
 * not valid as a React child") and blanks the page via the global
 * ErrorBoundary.
 *
 * This helper accepts either a string or an object and always returns a
 * user-friendly one-liner.
 */
export function formatUploadError(err) {
  if (err == null) return '';
  if (typeof err === 'string') return err;
  if (typeof err !== 'object') return String(err);

  const parts = [
    err.row != null ? `Row ${err.row}` : null,
    err.employee_id ? `[${err.employee_id}]` : null,
    err.field ? `${err.field}:` : null,
    err.message || err.error || err.code || 'error',
    err.matched_employee_code ? `(matches ${err.matched_employee_code})` : null,
  ].filter(Boolean);
  return parts.join(' ');
}

// ---- CSV export ---------------------------------------------------------

const CSV_COLUMNS = [
  { key: 'row', label: 'Row' },
  { key: 'employee_id', label: 'Employee ID' },
  { key: 'field', label: 'Field' },
  { key: 'code', label: 'Code' },
  { key: 'message', label: 'Message' },
  { key: 'matched_employee_code', label: 'Matched Employee Code' },
];

function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  // RFC 4180 — wrap in quotes and double any embedded quotes if the value
  // contains a comma, quote, or newline.
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Build a CSV string from a list of upload errors. Objects are exploded into
 * their named columns; plain-string errors go into the Message column with
 * the other columns left blank.
 */
export function buildUploadErrorsCsv(errors) {
  const header = CSV_COLUMNS.map((c) => c.label).join(',');
  const rows = (errors || []).map((err) => {
    if (typeof err === 'string' || err == null) {
      return CSV_COLUMNS.map((c) => (c.key === 'message' ? csvEscape(err) : '')).join(',');
    }
    return CSV_COLUMNS.map((c) => csvEscape(err[c.key])).join(',');
  });
  return [header, ...rows].join('\n');
}

/**
 * Trigger a browser download of the errors as a CSV file. `filename` should
 * end in `.csv` (default provided). Safe no-op if there are no errors.
 */
export function downloadUploadErrorsCsv(errors, filename = 'bulk-upload-errors.csv') {
  if (!errors || errors.length === 0) return;
  const csv = buildUploadErrorsCsv(errors);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---- Retry-template export (Excel) --------------------------------------

/**
 * Build an .xlsx workbook containing only the FAILED rows from a bulk-upload
 * attempt, preserving the original column headers so HR can fix cells
 * inline and re-upload the file via the same bulk-upload endpoint.
 *
 * A trailing "_Error Reason" column is appended (human-readable) — the
 * upload endpoint ignores unknown columns, so the file is re-uploadable
 * as-is once the offending cells are fixed.
 *
 * Requires `xlsx` to be installed (already in package.json).
 * `columnHeaders` should be the ordered list of header labels returned by
 * the upload endpoint (`column_headers`). If missing, falls back to
 * whatever keys appear across `error.row_data` dicts.
 */
export async function downloadUploadRetryTemplate(
  errors,
  columnHeaders,
  filename = 'bulk-upload-retry.xlsx',
) {
  if (!errors || errors.length === 0) return;
  // Only rows that actually carry row_data can be re-uploaded — plain-string
  // errors (e.g. "invalid file format") can't be re-tried at the row level.
  const rowsWithData = errors.filter((e) => e && typeof e === 'object' && e.row_data);
  if (rowsWithData.length === 0) return;

  // Resolve column order.
  let headers = Array.isArray(columnHeaders) && columnHeaders.length > 0
    ? [...columnHeaders]
    : [];
  if (headers.length === 0) {
    const seen = new Set();
    for (const e of rowsWithData) {
      for (const k of Object.keys(e.row_data || {})) {
        if (!seen.has(k)) { seen.add(k); headers.push(k); }
      }
    }
  }
  const errorCol = '_Error Reason';
  const outHeaders = [...headers, errorCol];

  const dataRows = rowsWithData.map((e) => {
    const record = {};
    for (const h of headers) {
      record[h] = (e.row_data && e.row_data[h] != null) ? e.row_data[h] : '';
    }
    record[errorCol] = formatUploadError(e);
    return record;
  });

  // Dynamic import so pages that never trigger a download don't pay the
  // xlsx bundle cost on initial load.
  const XLSX = await import('xlsx');
  const ws = XLSX.utils.json_to_sheet(dataRows, { header: outHeaders });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Retry');
  XLSX.writeFile(wb, filename);
}

